import type { LucideIcon } from 'lucide-react';
import {
  User,
  Plug,
  DollarSign,
  Users,
  GanttChart,
  Palette,
  Wrench,
  Key,
  Shield,
  FileText,
  Rocket,
  Trophy,
  Bug,
} from 'lucide-react';

export type SettingsSectionId =
  | 'account'
  | 'integrations'
  | 'proposals'
  | 'client-experience'
  | 'project-defaults'
  | 'team'
  | 'crm'
  | 'tools'
  | 'api'
  | 'agent-api-keys'
  | 'release-notes'
  | 'achievements'
  | 'feedback'
  | 'admin';

export type SettingsNavTier = 'personal' | 'workspace' | 'advanced' | 'admin';

export interface SettingsNavItem {
  id: SettingsSectionId;
  label: string;
  description: string;
  icon: LucideIcon;
  tier: SettingsNavTier;
  /** Hide unless user has admin role */
  adminOnly?: boolean;
}

export const SETTINGS_NAV_GROUPS: { tier: SettingsNavTier; label: string; collapsible: boolean; defaultCollapsed: boolean }[] = [
  { tier: 'personal', label: 'Personal', collapsible: false, defaultCollapsed: false },
  { tier: 'workspace', label: 'Workspace', collapsible: true, defaultCollapsed: false },
  { tier: 'advanced', label: 'Advanced', collapsible: true, defaultCollapsed: true },
  { tier: 'admin', label: 'Admin', collapsible: true, defaultCollapsed: true },
];

export const SETTINGS_NAV_ITEMS: SettingsNavItem[] = [
  {
    id: 'account',
    label: 'My Account',
    description: 'Profile, contact info, and notification preferences',
    icon: User,
    tier: 'personal',
  },
  {
    id: 'integrations',
    label: 'Integrations',
    description: 'Sendblue, QuickBooks, Sequence, Slack, Google Calendar, AI models',
    icon: Plug,
    tier: 'advanced',
  },
  {
    id: 'proposals',
    label: 'Proposal Rates',
    description: 'Design, production, install, and travel rate defaults',
    icon: DollarSign,
    tier: 'workspace',
  },
  {
    id: 'client-experience',
    label: 'Client Experience',
    description: 'Engagement docs, portal branding, raise-board photos, and questionnaire',
    icon: FileText,
    tier: 'workspace',
  },
  {
    id: 'project-defaults',
    label: 'Project Defaults',
    description: 'Timeline phases, onboarding checklist, project numbering, purchasing authority',
    icon: GanttChart,
    tier: 'workspace',
  },
  {
    id: 'team',
    label: 'Team',
    description: 'Opens People → Team (users, invites, and portals)',
    icon: Users,
    tier: 'admin',
    adminOnly: true,
  },
  {
    id: 'crm',
    label: 'CRM',
    description: 'Lead source colors, call automation, and bidding samples',
    icon: Palette,
    tier: 'advanced',
  },
  {
    id: 'tools',
    label: 'Tools',
    description: 'Calculator links, imports, and batch geocode',
    icon: Wrench,
    tier: 'advanced',
  },
  {
    id: 'api',
    label: 'API & Agents',
    description: 'Mac-mini agent gateway and catalog',
    icon: Key,
    tier: 'advanced',
  },
  {
    id: 'agent-api-keys',
    label: 'Agent API Keys',
    description: 'Issue, edit permissions, revoke, and monitor scoped employee / service-bot keys',
    icon: Shield,
    tier: 'admin',
    adminOnly: true,
  },
  {
    id: 'release-notes',
    label: 'Release Notes',
    description: 'Review and publish post-deploy release announcements',
    icon: Rocket,
    tier: 'admin',
    adminOnly: true,
  },
  {
    id: 'achievements',
    label: 'Certifications & Achievements',
    description: 'Define award icons and descriptions for employee recognition',
    icon: Trophy,
    tier: 'admin',
    adminOnly: true,
  },
  {
    id: 'feedback',
    label: 'Feedback',
    description: 'Review bug reports and feature requests from the CRM',
    icon: Bug,
    tier: 'admin',
    adminOnly: true,
  },
  {
    id: 'admin',
    label: 'Admin',
    description: 'People Hub benefits and system configuration',
    icon: Shield,
    tier: 'admin',
    adminOnly: true,
  },
];

export function getSettingsNavItem(id: string): SettingsNavItem | undefined {
  return SETTINGS_NAV_ITEMS.find(item => item.id === id);
}

export const SETTINGS_COLLAPSE_STORAGE_KEY = 'settings_nav_collapsed_tiers';
